import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import walkinDriveService from '../../services/walkinDriveService';
import clientService from '../../services/clientService';
import { QRCodeSVG } from 'qrcode.react';

const WalkinDrivesPage = () => {
  const { user, userProfile } = useAuth();
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [drives, setDrives] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedQRDrive, setSelectedQRDrive] = useState(null);
  const [selectedApplicantsDrive, setSelectedApplicantsDrive] = useState(null);
  const [applicantsList, setApplicantsList] = useState([]);
  const [applicantsLoading, setApplicantsLoading] = useState(false);
  const [applicantFilterPanel, setApplicantFilterPanel] = useState('all');
  const [applicantFilterStatus, setApplicantFilterStatus] = useState('all');
  const [copiedToken, setCopiedToken] = useState(false);
  const [printMode, setPrintMode] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    venue: '',
    location: '',
    drive_date_start: new Date().toISOString().split('T')[0],
    drive_date_end: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
    role_requirements: '',
    panel_count: 4,
    status: 'active'
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Load drives
  const loadDrives = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      console.log('Fetching walk-in drives with params:', params);
      const res = await walkinDriveService.getDrives(params);
      console.log('Walk-in drives response:', res);
      if (res.success && res.data) {
        setDrives(res.data);
        console.log(`Successfully loaded ${res.data.length} walk-in drive(s) into dashboard`);
      } else {
        throw new Error(res.error || 'Failed to load walk-in drives');
      }
    } catch (err) {
      console.error('Error loading walk-in drives:', err);
      setError(err.message || 'Failed to load walk-in drives');
    } finally {
      setLoading(false);
    }
  };

  // Load clients for dropdown
  const loadClients = async () => {
    try {
      const res = await clientService.getClients();
      if (res.success && res.data) {
        setClients(res.data);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  useEffect(() => {
    loadDrives();
    loadClients();
  }, [statusFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadDrives();
  };

  // Create Form Handler
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    if (!formData.title.trim() || !formData.venue.trim() || !formData.location.trim()) {
      setFormError('Please fill in all mandatory fields');
      setSubmitting(false);
      return;
    }

    try {
      console.log('Creating new walk-in drive payload:', formData);
      const res = await walkinDriveService.createDrive(formData);
      console.log('Create walk-in drive response:', res);
      if (res.success && res.data) {
        setShowCreateModal(false);
        setFormData({
          title: '',
          client_id: '',
          venue: '',
          location: '',
          drive_date_start: new Date().toISOString().split('T')[0],
          drive_date_end: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
          role_requirements: '',
          panel_count: 4,
          status: 'active'
        });
        setStatusFilter('all');
        setSearchQuery('');
        console.log('Refetching drives list immediately after creation...');
        await loadDrives();
        setSelectedQRDrive(res.data); // Open QR modal automatically for the new drive
      } else {
        setFormError(res.error || 'Failed to create walk-in drive');
      }
    } catch (err) {
      setFormError(err.message || 'Error creating walk-in drive');
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle Drive Status
  const handleToggleStatus = async (drive) => {
    const nextStatus = drive.status === 'active' ? 'closed' : 'active';
    const confirmMsg = nextStatus === 'closed' 
      ? `Are you sure you want to CLOSE "${drive.title}"? Candidates will no longer be able to apply.`
      : `Re-activate "${drive.title}"?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await walkinDriveService.updateDrive(drive.id, { status: nextStatus });
      if (res.success) {
        loadDrives();
      } else {
        alert(res.error || 'Failed to update drive status');
      }
    } catch (err) {
      alert(err.message || 'Error updating status');
    }
  };

  // Delete Drive
  const handleDeleteDrive = async (drive) => {
    if (!window.confirm(`Are you sure you want to delete "${drive.title}" and all its walk-in records? This action cannot be undone.`)) {
      return;
    }
    try {
      const res = await walkinDriveService.deleteDrive(drive.id);
      if (res.success) {
        loadDrives();
      } else {
        alert(res.error || 'Failed to delete walk-in drive');
      }
    } catch (err) {
      alert(err.message || 'Error deleting drive');
    }
  };

  // View Applicants Modal
  const handleOpenApplicants = async (drive) => {
    setSelectedApplicantsDrive(drive);
    setApplicantFilterPanel('all');
    setApplicantFilterStatus('all');
    try {
      setApplicantsLoading(true);
      const res = await walkinDriveService.getApplicants(drive.id);
      if (res.success && res.data) {
        setApplicantsList(res.data);
      }
    } catch (err) {
      console.error('Failed to load drive applicants:', err);
    } finally {
      setApplicantsLoading(false);
    }
  };

  const reloadApplicants = async () => {
    if (!selectedApplicantsDrive) return;
    try {
      setApplicantsLoading(true);
      const params = {};
      if (applicantFilterPanel && applicantFilterPanel !== 'all') {
        params.panel_number = applicantFilterPanel;
      }
      if (applicantFilterStatus && applicantFilterStatus !== 'all') {
        params.status = applicantFilterStatus;
      }
      const res = await walkinDriveService.getApplicants(selectedApplicantsDrive.id, params);
      if (res.success && res.data) {
        setApplicantsList(res.data);
      }
    } catch (err) {
      console.error('Error reloading applicants:', err);
    } finally {
      setApplicantsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedApplicantsDrive) {
      reloadApplicants();
    }
  }, [applicantFilterPanel, applicantFilterStatus]);

  // QR Code Actions
  const getPublicApplyUrl = (token) => {
    return `${window.location.origin}/walkin-apply/${token}`;
  };

  const handleCopyLink = (token) => {
    navigator.clipboard.writeText(getPublicApplyUrl(token));
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2500);
  };

  const handleDownloadQR = (drive) => {
    const svg = document.getElementById(`drive-qr-${drive.id}`)?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.onload = () => {
      canvas.width = img.width + 40;
      canvas.height = img.height + 40;
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 20, 20);
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = `${drive.title.replace(/[^a-z0-9]/gi, '_')}-QR.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
      }
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handlePrintPoster = () => {
    window.print();
  };

  // Totals for top cards
  const totalDrives = drives.length;
  const activeDrives = drives.filter(d => d.status === 'active').length;
  const totalCandidates = drives.reduce((acc, d) => acc + (d.stats?.total_applicants || 0), 0);
  const totalSelected = drives.reduce((acc, d) => acc + (d.stats?.selected || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={userProfile || user}
        notifications={[]}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          <BreadcrumbNavigation />

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 pb-6 border-b border-border">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name="QrCode" size={26} />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Walk-in Hiring Drives</h1>
                  <p className="text-sm text-muted-foreground">
                    QR-code-based walk-in hiring system with live shortest-queue panel assignment and instant ATS promotion
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={loadDrives}
              >
                Refresh
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowCreateModal(true)}
                className="shadow-md"
              >
                Create New Drive
              </Button>
            </div>
          </div>

          {/* KPI Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Drives</p>
                  <h3 className="text-2xl font-bold text-foreground mt-1">{totalDrives}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                  <Icon name="Briefcase" size={22} />
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">All hiring drives configured</div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Active Drives</p>
                  <h3 className="text-2xl font-bold text-success mt-1">{activeDrives}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-success/10 text-success flex items-center justify-center">
                  <Icon name="Activity" size={22} />
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">Live & ready to accept walk-ins</div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Candidates</p>
                  <h3 className="text-2xl font-bold text-foreground mt-1">{totalCandidates}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                  <Icon name="Users" size={22} />
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">Scanned & queued on-site</div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Selected & Promoted</p>
                  <h3 className="text-2xl font-bold text-primary mt-1">{totalSelected}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name="UserCheck" size={22} />
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">Promoted into ATS pipeline</div>
            </div>
          </div>

          {/* Filter & Search Bar */}
          <div className="bg-card border border-border rounded-2xl p-4 mb-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 w-full md:w-auto">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-2">Status:</span>
              {['all', 'active', 'closed'].map(st => (
                <button
                  key={st}
                  onClick={() => setStatusFilter(st)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
                    statusFilter === st
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 w-full md:w-72">
              <div className="relative flex-1">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search drive, venue, city..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <Button type="submit" size="sm" variant="secondary">Search</Button>
            </form>
          </div>

          {/* Drives List */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-card rounded-2xl border border-border">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-sm text-muted-foreground">Loading walk-in drives...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center bg-card rounded-2xl border border-error/20 text-error">
              <Icon name="AlertCircle" size={40} className="mx-auto mb-2 opacity-80" />
              <p className="font-semibold">{error}</p>
              <Button variant="outline" size="sm" onClick={loadDrives} className="mt-3">Retry</Button>
            </div>
          ) : drives.length === 0 ? (
            <div className="text-center py-16 bg-card rounded-2xl border border-dashed border-border">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
                <Icon name="QrCode" size={32} />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-1">No Walk-in Drives Found</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
                Create a walk-in hiring drive to generate QR codes, organize live panel queues, and onboard candidates instantly.
              </p>
              <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowCreateModal(true)}>
                Create First Walk-in Drive
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {drives.map(drive => (
                <div
                  key={drive.id}
                  className="bg-card border border-border rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all flex flex-col justify-between"
                >
                  <div>
                    {/* Status Badge & Actions Header */}
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        drive.status === 'active' 
                          ? 'bg-success/10 text-success border border-success/20' 
                          : 'bg-muted text-muted-foreground border border-border'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${drive.status === 'active' ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`} />
                        {drive.status === 'active' ? 'Active Drive' : 'Closed'}
                      </span>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedQRDrive(drive)}
                          title="View QR Code"
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                        >
                          <Icon name="QrCode" size={18} />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(drive)}
                          title={drive.status === 'active' ? 'Close Drive' : 'Reactivate Drive'}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        >
                          <Icon name={drive.status === 'active' ? 'Lock' : 'Unlock'} size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteDrive(drive)}
                          title="Delete Drive"
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-error hover:bg-error/10 transition-colors"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Drive Title & Client */}
                    <h3 className="text-xl font-bold text-foreground mb-1 line-clamp-1">{drive.title}</h3>
                    {drive.client_name ? (
                      <div className="flex items-center gap-1.5 text-xs text-primary font-semibold mb-3">
                        <Icon name="Building2" size={14} />
                        <span>{drive.client_name}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-3">
                        <Icon name="Building" size={14} />
                        <span>General / InChi Internal Drive</span>
                      </div>
                    )}

                    {/* Location & Dates */}
                    <div className="space-y-1.5 text-xs text-muted-foreground mb-4 p-3 bg-muted/20 rounded-xl border border-border">
                      <div className="flex items-start gap-2">
                        <Icon name="MapPin" size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1"><strong className="text-foreground">{drive.location}:</strong> {drive.venue}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={14} className="text-muted-foreground flex-shrink-0" />
                        <span>{drive.drive_date_start} to {drive.drive_date_end}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Layers" size={14} className="text-muted-foreground flex-shrink-0" />
                        <span><strong className="text-foreground">{drive.panel_count} Live Panels</strong> configured</span>
                      </div>
                    </div>

                    {/* Role preview */}
                    {drive.role_requirements && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-4">
                        <strong className="text-foreground">Roles:</strong> {drive.role_requirements}
                      </p>
                    )}

                    {/* Stats summary */}
                    <div className="grid grid-cols-4 gap-2 text-center p-2.5 bg-muted/40 rounded-xl border border-border mb-4">
                      <div>
                        <div className="text-sm font-bold text-foreground">{drive.stats?.total_applicants || 0}</div>
                        <div className="text-[10px] text-muted-foreground font-medium">Applied</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-blue-500">{drive.stats?.queued || 0}</div>
                        <div className="text-[10px] text-muted-foreground font-medium">Queued</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-amber-500">{drive.stats?.interviewing || 0}</div>
                        <div className="text-[10px] text-muted-foreground font-medium">In Interview</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-success">{drive.stats?.selected || 0}</div>
                        <div className="text-[10px] text-muted-foreground font-medium">Selected</div>
                      </div>
                    </div>
                  </div>

                  {/* Primary Buttons */}
                  <div className="space-y-2 pt-2 border-t border-border">
                    <Link
                      to={`/erp/walkin-drives/${drive.id}/panel`}
                      className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-primary text-primary-foreground font-semibold text-xs shadow-sm hover:bg-primary/90 transition-colors"
                    >
                      <Icon name="PlayCircle" size={16} />
                      Launch Live Panel Dashboard
                    </Link>

                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => setSelectedQRDrive(drive)}
                      >
                        <Icon name="QrCode" size={14} className="mr-1" />
                        QR Poster
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => handleOpenApplicants(drive)}
                      >
                        <Icon name="List" size={14} className="mr-1" />
                        Candidates ({drive.stats?.total_applicants || 0})
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CREATE DRIVE MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-xl w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="flex items-center justify-between pb-4 mb-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name="Plus" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground">Create Walk-in Hiring Drive</h2>
                  <p className="text-xs text-muted-foreground">Setup drive venue, panel capacity, and generate dynamic QR code</p>
                </div>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>

            {formError && (
              <div className="p-3 mb-4 rounded-xl bg-error/10 border border-error/20 text-error text-xs flex items-center gap-2">
                <Icon name="AlertCircle" size={16} />
                <span>{formError}</span>
              </div>
            )}

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Drive Title *</label>
                <input
                  type="text"
                  name="title"
                  required
                  placeholder="e.g. InChi Mega Walk-in Hiring Drive 2026"
                  value={formData.title}
                  onChange={handleFormChange}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Client Company (Optional)</label>
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleFormChange}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">General Drive / Multiple Clients</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.company_name} ({c.industry || 'Client'})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">City / Location *</label>
                  <input
                    type="text"
                    name="location"
                    required
                    placeholder="e.g. Bangalore"
                    value={formData.location}
                    onChange={handleFormChange}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Live Panel Count *</label>
                  <select
                    name="panel_count"
                    value={formData.panel_count}
                    onChange={handleFormChange}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value={2}>2 Panels</option>
                    <option value={3}>3 Panels</option>
                    <option value={4}>4 Panels (Default)</option>
                    <option value={5}>5 Panels</option>
                    <option value={6}>6 Panels</option>
                    <option value={8}>8 Panels</option>
                    <option value={10}>10 Panels</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Venue Address *</label>
                <textarea
                  name="venue"
                  required
                  rows={2}
                  placeholder="e.g. 19/A, 3rd Floor, Basaveshwar Nagar 1st Main Rd, Bangalore - 560079"
                  value={formData.venue}
                  onChange={handleFormChange}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Start Date *</label>
                  <input
                    type="date"
                    name="drive_date_start"
                    required
                    value={formData.drive_date_start}
                    onChange={handleFormChange}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">End Date *</label>
                  <input
                    type="date"
                    name="drive_date_end"
                    required
                    value={formData.drive_date_end}
                    onChange={handleFormChange}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Roles / Key Requirements</label>
                <textarea
                  name="role_requirements"
                  rows={3}
                  placeholder="e.g. Java Fullstack Developers, React Engineers, Quality Assurance, Technical Support Executives"
                  value={formData.role_requirements}
                  onChange={handleFormChange}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="default" disabled={submitting}>
                  {submitting ? 'Generating Drive...' : 'Create Drive & Generate QR'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR CODE & PRINTABLE POSTER MODAL */}
      {selectedQRDrive && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8 animate-fade-in-up text-center">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-border">
              <h3 className="font-bold text-foreground text-lg">Drive QR Code & Apply Link</h3>
              <button onClick={() => setSelectedQRDrive(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Printable Area */}
            <div id={`drive-qr-${selectedQRDrive.id}`} className="bg-white p-6 rounded-2xl border border-border shadow-inner mb-5 text-gray-900 inline-block w-full">
              <h2 className="text-lg font-bold text-gray-900 mb-1">{selectedQRDrive.title}</h2>
              {selectedQRDrive.client_name && (
                <p className="text-xs font-semibold text-indigo-600 mb-2">{selectedQRDrive.client_name}</p>
              )}
              <div className="p-3 bg-gray-50 rounded-xl inline-block my-2 border border-gray-200">
                <QRCodeSVG
                  value={getPublicApplyUrl(selectedQRDrive.qr_token)}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p className="text-xs font-bold tracking-wide text-gray-700 mt-2 uppercase">Scan to Apply On-site</p>
              <div className="text-[11px] text-gray-500 mt-2 space-y-0.5">
                <p>📍 {selectedQRDrive.venue}, {selectedQRDrive.location}</p>
                <p>📅 {selectedQRDrive.drive_date_start} to {selectedQRDrive.drive_date_end}</p>
                <p>⚡ Instant Interview Panel Assignment</p>
              </div>
            </div>

            {/* Public Link Box */}
            <div className="p-3 bg-muted/40 rounded-xl border border-border text-left mb-5">
              <label className="text-[11px] font-semibold text-muted-foreground block mb-1">Public Application URL</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={getPublicApplyUrl(selectedQRDrive.qr_token)}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs font-mono text-foreground focus:outline-none"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCopyLink(selectedQRDrive.qr_token)}
                  className="flex-shrink-0"
                >
                  <Icon name={copiedToken ? "Check" : "Copy"} size={14} className="mr-1" />
                  {copiedToken ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="secondary"
                onClick={() => handleDownloadQR(selectedQRDrive)}
                className="w-full justify-center"
              >
                <Icon name="Download" size={16} className="mr-2" />
                Download PNG
              </Button>
              <Button
                variant="default"
                onClick={handlePrintPoster}
                className="w-full justify-center"
              >
                <Icon name="Printer" size={16} className="mr-2" />
                Print Poster
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* APPLICANTS VIEWER MODAL */}
      {selectedApplicantsDrive && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-4xl w-full p-6 md:p-8 max-h-[90vh] flex flex-col animate-fade-in-up">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-border flex-shrink-0">
              <div>
                <h3 className="font-bold text-foreground text-xl">Candidates: {selectedApplicantsDrive.title}</h3>
                <p className="text-xs text-muted-foreground">Total {applicantsList.length} walk-in candidate(s) registered for this drive</p>
              </div>
              <button onClick={() => setSelectedApplicantsDrive(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={22} />
              </button>
            </div>

            {/* Filters Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3 mb-4 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-3">
                <select
                  value={applicantFilterPanel}
                  onChange={(e) => setApplicantFilterPanel(e.target.value)}
                  className="px-3 py-1.5 rounded-xl border border-border bg-background text-xs font-semibold text-foreground focus:outline-none"
                >
                  <option value="all">All Panels (1 to {selectedApplicantsDrive.panel_count})</option>
                  {Array.from({ length: selectedApplicantsDrive.panel_count }, (_, i) => i + 1).map(p => (
                    <option key={p} value={p}>Panel {p}</option>
                  ))}
                </select>

                <select
                  value={applicantFilterStatus}
                  onChange={(e) => setApplicantFilterStatus(e.target.value)}
                  className="px-3 py-1.5 rounded-xl border border-border bg-background text-xs font-semibold text-foreground focus:outline-none"
                >
                  <option value="all">All Statuses</option>
                  <option value="queued">Queued</option>
                  <option value="interviewing">Interviewing</option>
                  <option value="selected">Selected</option>
                  <option value="not_selected">Not Selected</option>
                </select>
              </div>

              <Link
                to={`/erp/walkin-drives/${selectedApplicantsDrive.id}/panel`}
                className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
              >
                Launch Live Panel Dashboard <Icon name="ExternalLink" size={12} />
              </Link>
            </div>

            {/* Candidates Table */}
            <div className="flex-1 overflow-y-auto min-h-[300px]">
              {applicantsLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mb-2"></div>
                  <p className="text-xs text-muted-foreground">Loading candidates...</p>
                </div>
              ) : applicantsList.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground text-sm">
                  No applicants found matching selected filters.
                </div>
              ) : (
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-muted-foreground font-semibold">
                      <th className="p-3">Candidate</th>
                      <th className="p-3">Position</th>
                      <th className="p-3">Panel</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Rating / Feedback</th>
                      <th className="p-3">Applied At</th>
                      <th className="p-3 text-right">ATS Link</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {applicantsList.map(app => (
                      <tr key={app.id} className="hover:bg-muted/20 transition-colors">
                        <td className="p-3">
                          <div className="font-bold text-foreground">{app.name}</div>
                          <div className="text-muted-foreground text-[11px]">{app.phone} • {app.email}</div>
                          {app.resume_url && (
                            <a
                              href={`/${app.resume_url}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 mt-0.5"
                            >
                              <Icon name="FileText" size={11} /> View Resume
                            </a>
                          )}
                        </td>
                        <td className="p-3 font-medium text-foreground">{app.position_interested}</td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-bold">
                            Panel {app.panel_number}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full font-semibold capitalize ${
                            app.status === 'selected' ? 'bg-success/10 text-success' :
                            app.status === 'interviewing' ? 'bg-amber-500/10 text-amber-500' :
                            app.status === 'not_selected' ? 'bg-error/10 text-error' :
                            'bg-blue-500/10 text-blue-500'
                          }`}>
                            {app.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="p-3">
                          {app.interview_rating ? (
                            <div className="flex items-center gap-1 font-bold text-amber-500 mb-0.5">
                              <Icon name="Star" size={12} fill="currentColor" /> {app.interview_rating} / 5
                            </div>
                          ) : null}
                          {app.interview_notes && (
                            <p className="text-muted-foreground text-[11px] line-clamp-1 italic">"{app.interview_notes}"</p>
                          )}
                        </td>
                        <td className="p-3 text-muted-foreground whitespace-nowrap">{app.applied_at}</td>
                        <td className="p-3 text-right">
                          {app.promoted_to_application_id ? (
                            <Link
                              to={`/erp/applicant-tracking-system?applicantId=${app.promoted_to_application_id}`}
                              className="px-2.5 py-1 rounded-lg bg-success/10 text-success font-semibold hover:bg-success/20 transition-colors inline-flex items-center gap-1"
                            >
                              <Icon name="CheckCircle" size={12} /> In ATS
                            </Link>
                          ) : (
                            <span className="text-muted-foreground text-[11px]">Walk-in only</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t border-border mt-4 flex-shrink-0">
              <Button variant="outline" onClick={() => setSelectedApplicantsDrive(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalkinDrivesPage;
